import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT, LangToggle } from "@/lib/i18n";
import { Logo } from "@/components/Logo";
import { errMsg } from "@/lib/api";
import { GoogleButton } from "@/pages/AuthCallback";
import { PasswordInput } from "@/components/PasswordInput";

export function AuthShell({ title, children, footer }) {
  return (
    <div className="mo-grid-bg flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rise">
        <div className="mb-8 flex items-center justify-between"><Logo /><LangToggle /></div>
        <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.2)]">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <div className="mt-6">{children}</div>
        </div>
        <p className="mt-6 text-center text-sm text-slate-600">{footer}</p>
      </div>
    </div>
  );
}

export const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#5aa469] focus:ring-2 focus:ring-[#5aa469]/20";
export const btnCls = "w-full rounded-full bg-[#5aa469] py-3 font-semibold text-white hover:bg-[#4a8a56] transition-colors disabled:opacity-60";

export default function Login() {
  const { t } = useT();
  const { login, staffLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const qrCode = params.get("code") || "";
  const [mode, setMode] = useState(qrCode ? "staff" : "owner");
  const [f, setF] = useState({ email: "", password: "" });
  const [code, setCode] = useState(qrCode.toUpperCase());
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!qrCode) return;
    setBusy(true);
    staffLogin(qrCode).then(() => nav("/app", { replace: true })).catch((ex) => setErr(errMsg(ex))).finally(() => setBusy(false));
  }, [qrCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(f.email, f.password); nav("/app"); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const submitStaff = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await staffLogin(code); nav("/app"); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const tab = (m) => `flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${mode === m ? "bg-[#5aa469] text-white" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <AuthShell title={mode === "owner" ? t("login") : t("staff_access")} footer={<>{t("no_account")} <Link to="/register" data-testid="go-register-link" className="font-semibold text-[#4a8a56]">{t("register")}</Link></>}>
      <div className="mb-5 flex gap-1 rounded-full bg-slate-50 p-1 ring-1 ring-slate-200">
        <button type="button" data-testid="login-tab-owner" className={tab("owner")} onClick={() => { setMode("owner"); setErr(""); }}>{t("owner")}</button>
        <button type="button" data-testid="login-tab-staff" className={tab("staff")} onClick={() => { setMode("staff"); setErr(""); }}>{t("staff")}</button>
      </div>
      {mode === "staff" ? (
        <form onSubmit={submitStaff} className="space-y-4" data-testid="staff-login-form">
          <p className="text-sm text-slate-600">{qrCode && busy ? t("qr_logging_in") : t("staff_access_hint")}</p>
          <input data-testid="staff-code-input" required autoFocus autoCapitalize="characters" autoComplete="off" placeholder="ES. ROSSI-4821" className={inputCls + " text-center font-heading text-xl font-extrabold tracking-widest uppercase"} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          {err && <p data-testid="login-error" className="text-sm text-rose-600">{err}</p>}
          <button data-testid="staff-login-submit-btn" disabled={busy} className={btnCls}>{t("enter_shift")}</button>
        </form>
      ) : (<>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email-input" type="email" required placeholder={t("email")} className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <PasswordInput testId="login-password-input" required placeholder={t("password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="text-right"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-[#4a8a56] hover:underline">{t("forgot_link")}</Link></div>
        {err && <p data-testid="login-error" className="text-sm text-rose-600">{err}</p>}
        <button data-testid="login-submit-btn" disabled={busy} className={btnCls}>{t("login")}</button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />{t("or")}<span className="h-px flex-1 bg-slate-200" /></div>
      <GoogleButton label={t("google_login")} />
      </>)}
    </AuthShell>
  );
}
